import { isLoggedInAtom } from '@/state'
import { getDefaultStore } from 'jotai'
import { z } from 'zod'

export const HTTP_METHOD = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
}

type SafeArrayBufferView =
  | Uint8Array
  | Uint16Array
  | Uint32Array
  | Int8Array
  | Int16Array
  | Int32Array
  | Float32Array
  | Float64Array
  | DataView

type Payload =
  | string
  | Blob
  | File
  | FormData
  | ArrayBuffer
  | SafeArrayBufferView
  | URLSearchParams
  | object
  | undefined

type ApiResp<T> = {
  data: T
  status: number
}

type SchemaOrUnknown<S extends z.ZodTypeAny | undefined, T> = S extends z.ZodTypeAny
  ? z.infer<S>
  : T

async function request<
  T = unknown,
  S extends z.ZodTypeAny | undefined = undefined,
  R = SchemaOrUnknown<S, T>,
>(
  method: keyof typeof HTTP_METHOD,
  path: string,
  options?: {
    headers?: Record<string, string>
    queryParams?: Record<string, string | number | boolean>
    payload?: Payload
    credentials?: RequestCredentials
    forceJson?: boolean
  },
  schema?: S
): Promise<ApiResp<R>> {
  const { queryParams, payload } = options || {}

  const uri =
    queryParams && Object.keys(queryParams).length
      ? `${path}?${new URLSearchParams(
          Object.entries(queryParams)
            .filter(([_, v]) => v != null)
            .map(([k, v]) => [k, String(v)])
        )}`
      : path

  const headers = options?.headers || resolveHeaders(options?.headers, payload)

  const resp = await fetch(uri, {
    method,
    headers,
    credentials: options?.credentials ?? 'include',
    // credentials: 'same-origin',
    body: payload && bodyBuilder(payload),
  })

  if (resp.status === 401) {
    // logout
    localStorage.setItem('isLoggedIn', 'false')
    const store = getDefaultStore()
    store.set(isLoggedInAtom, false)
    fetch('/auth/v1/token', { method: 'DELETE', credentials: 'same-origin' }).catch(() => {})
  }

  if (resp.status === 403) {
    window.location.replace('/forbidden')
  }

  if (!resp.ok) {
    throw new Error(`HTTP error! Status: ${resp.status} Body: ${await resp.text()}`)
  }

  if (resp.status === 204 || resp.headers.get('content-length') === '0') {
    return { data: undefined as R, status: resp.status }
  }

  const contentType = resp.headers.get('content-type') || ''
  if (contentType.includes('application/json') || options?.forceJson) {
    const text = await resp.text()
    if (!text) {
      return { data: undefined as R, status: resp.status }
    }
    const data = JSON.parse(text)
    return { data: schema ? (schema.parse(data) as R) : data, status: resp.status }
  } else {
    const text = await resp.text()
    return { data: text as R, status: resp.status }
  }
}

export const get = <T = unknown, S extends z.ZodTypeAny | undefined = undefined>(
  path: string,
  options?: {
    headers?: Record<string, string>
    queryParams?: Record<string, string | number | boolean>
    credentials?: RequestCredentials
    forceJson?: boolean
  },
  schema?: S
) => request<T, S>('GET', path, options, schema)

export const del = <T = unknown, S extends z.ZodTypeAny | undefined = undefined>(
  path: string,
  options?: {
    headers?: Record<string, string>
    queryParams?: Record<string, string | number>
    credentials?: RequestCredentials
    forceJson?: boolean
  },
  schema?: S
) => request<T, S>('DELETE', path, options, schema)

export const post = <T = unknown, S extends z.ZodTypeAny | undefined = undefined>(
  path: string,
  options?: {
    headers?: Record<string, string>
    payload: object | File
    credentials?: RequestCredentials
    forceJson?: boolean
  },
  schema?: S
) => request<T, S>('POST', path, options, schema)

export const put = <T = unknown, S extends z.ZodTypeAny | undefined = undefined>(
  path: string,
  options?: {
    headers?: Record<string, string>
    payload: object | File
    credentials?: RequestCredentials
    forceJson?: boolean
  },
  schema?: S
) => request<T, S>('PUT', path, options, schema)

export const patch = <T = unknown, S extends z.ZodTypeAny | undefined = undefined>(
  path: string,
  options?: {
    headers?: Record<string, string>
    payload: object | File
    credentials?: RequestCredentials
    forceJson?: boolean
  },
  schema?: S
) => request<T, S>('PATCH', path, options, schema)

function resolveHeaders(
  headers: Record<string, string> | undefined,
  payload?: Payload
): Record<string, string> {
  const finalHeaders = { ...(headers || {}) }

  if (payload instanceof FormData || payload instanceof File || payload instanceof Blob) {
    delete finalHeaders['Content-Type']
  } else if (payload instanceof URLSearchParams) {
    finalHeaders['Content-Type'] = 'application/x-www-form-urlencoded'
  } else if (payload && !(payload instanceof ArrayBuffer) && !ArrayBuffer.isView(payload)) {
    finalHeaders['Content-Type'] = 'application/json'
  }

  return finalHeaders
}

function bodyBuilder(payload?: Payload): BodyInit | undefined {
  if (!payload) return undefined

  if (
    payload instanceof FormData ||
    payload instanceof Blob ||
    payload instanceof File ||
    payload instanceof ArrayBuffer ||
    ArrayBuffer.isView(payload) ||
    payload instanceof URLSearchParams
  ) {
    return payload as BodyInit
  }

  return JSON.stringify(payload)
}
